import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { VDialogRef } from '@app/directives/_layout/v-dialog/v-dialog-ref';
import { DialogService } from '@app/directives/_layout/v-dialog/v-dialog.service';
import { VTableComponent } from '@app/directives/_layout/v-table/_components/v-table.component';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { VTabComponent } from '@app/directives/_layout/v-tabs/v-tab.component';
import { SortDirection } from '@app/enum/SortDirection';
import { IConfigurableCameraDevice, IPeripheral } from '@app/interfaces/IPeripheral';
import { IUserIdToNameMapping } from '@app/main/modules/foundation/foundation.interfaces';
import { CommServerRequestState } from '@app/main/modules/foundation/_routes/hardware/_enums/CommServerRequestState';
import { IPeripheralMetadata } from "@app/main/modules/foundation/_routes/hardware/_models/IPeripheralMetadata";
import { CameraApplicationState, CameraAppType, ICameraApp } from '@app/main/modules/foundation/_routes/rv-lite/application/_models/ICameraApp';
import { CameraAppService } from '@app/main/modules/foundation/_routes/rv-lite/application/_services/camera-app.service';
import { CameraApplicationRequestService } from '@app/main/modules/foundation/_routes/rv-lite/application/_services/cameraApplicationRequestService';
import { IApplicationCameraRequest, IConfigCameraRequest } from '@app/main/modules/foundation/_routes/rv-lite/camera-config/camera-configuration.models';
import { ICameraConfiguration } from '@app/main/modules/foundation/_routes/rv-lite/config/_models/ICameraConfiguration';
import { CameraConfigurationRequestService } from '@app/main/modules/foundation/_routes/rv-lite/config/_services/cameraConfigurationRequestService';
import { UserService } from '@app/main/modules/foundation/_services/user.service';
import { CameraCommunicationServerService } from '@app/services/camera/camera-communication-server.service';
import { IConfigurationFile, IConfigurationUpdate } from '@app/services/cameraCommunicationServer.service';
import { CurrentUserService } from '@app/services/currentUser.service';
import { DateService, DateUnit } from '@app/services/date.service';
import { TranslationService } from '@app/services/translation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, EMPTY, forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import './config-switcher.component.scss';

@UntilDestroy()
@Component({
    selector: 'config-switcher',
    template: require('./config-switcher.component.html')
})
export class ConfigSwitcherComponent implements OnInit {
    // shared inputs
    @Input() isDeviceSpecific: boolean;
    @Input() allConfigs: ICameraConfiguration[];

    // inputs from device specific overview
    @Input() peripheral: IPeripheral;
    @Input() peripheralMetadata: IPeripheralMetadata;
    @Input() veriConfigHistory: IConfigCameraRequest[];

    //  Inputs from multi-select table
    @Input() selectedRows: IVTableRow[];
    @Input() selectedDeviceColumns: IVTableColumn<IConfigurableCameraDevice>[];
    @Input() selectedDevices: IPeripheral[] = [];

    @Output() onActionsSet = new EventEmitter<any>();
    @Output() onAutoUpdateSet = new EventEmitter<boolean>();

    // tables
    configHistoryTableColumns: IVTableColumn<IConfigurationUpdate>[] = [];
    configHistoryTableRows: IVTableRow[] = [];
    updaterHistoryTableColumns: IVTableColumn<IApplicationCameraRequest>[] = []
    updaterHistoryTableRows: IVTableRow[] = [];
    telematicsHistoryTableColumns: IVTableColumn<IApplicationCameraRequest>[] = []
    telematicsHistoryTableRows: IVTableRow[] = [];

    loading: boolean = false;
    loadingTable: boolean = false;

    availableConfigs: ICameraConfiguration[];
    allApps: ICameraApp[];
    availableTelematicsApps: ICameraApp[];
    availableUpdaterApps: ICameraApp[];

    telematicsAppToApply: ICameraApp;
    updaterAppToApply: ICameraApp;
    configToApply: ICameraConfiguration;
    forceUpdate: boolean = false;

    userToNameList: IUserIdToNameMapping[];

    selectedTab: VTabComponent;

    changeUpdateList: string[] = [];

    @ViewChild(VTableComponent) table: VTableComponent;
    @ViewChild('confirmApplyTemplate') confirmApplyTemplate: TemplateRef<any>;
    dialog: VDialogRef;

    // Commserver fields to be deleted when Commserver goes
    currentConfigId: number;
    deviceTypeId: number;
    deviceId: number;
    configUpdateHistory: IConfigurationUpdate[] = [];
    configFiles: IConfigurationFile[];
    selectedConfigFile: IConfigurationFile;

    get formIsCompletable(): boolean {
        return !!this.telematicsAppToApply?.Id ||
            !!this.updaterAppToApply?.Id ||
            !!this.configToApply?.Id;
    }

    get showApplyButton(): boolean {
        return this.selectedTab?.tabId == "apply"
    }

    get getConfirmationString(): string {
        return `${this.selectedDevices?.length > 1 ? this.selectedDevices?.length + this.translationService.t("ConfigManagement.DevicesLowerCase") : this.selectedDevices[0].Serial} ${this.translationService.t("ConfigManagement.ChangesBlurb")}`;
    }

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly cameraCommunicationServer: CameraCommunicationServerService,
        private readonly vTableService: VTableService,
        private readonly cameraAppService: CameraAppService,
        private readonly userService: UserService,
        private readonly cameraConfigurationRequestService: CameraConfigurationRequestService,
        private readonly cameraApplicationRequestService: CameraApplicationRequestService,
        private readonly dateService: DateService,
        private readonly currentUserService: CurrentUserService,
        private readonly translationService: TranslationService,
        private readonly dialogService: DialogService
    ) { }

    ngOnInit(): void {
        this.createTables();
        this.loadData()
            .pipe(untilDestroyed(this)).subscribe({
                next: () => this.loading = false
            });
    }

    setForceUpdate($event: any): void {
        this.forceUpdate = $event;
    }

    onSelectedTabChanged(tab: VTabComponent): void {
        this.selectedTab = tab;
    }

    confirmSaveAndCloseDialog(): void {
        this.closeDialog();

        const observablesArray: Observable<any>[] = [];

        this.selectedDevices.forEach(device => {
            if (this.configToApply?.Id) {
                const newRequest: IConfigCameraRequest = {
                    Id: "",
                    ForceUpdate: this.forceUpdate,
                    RequestState: CommServerRequestState.Pending,
                    ConfigurationId: this.configToApply.Id,
                    Serial: device.Serial,
                    Expires: this.dateService.addOrSubtractTime(new Date, 1, DateUnit.Day),
                    UserId: this.currentUserService.cachedUser.Id,
                    VehicleId: device.vehicleMapping?.VehicleId
                }

                observablesArray.push(this.cameraConfigurationRequestService.postConfigurationRequest(newRequest));
            }

            if (this.telematicsAppToApply?.Id) {
                const newRequest: IApplicationCameraRequest = {
                    Id: "",
                    ForceUpdate: this.forceUpdate,
                    RequestState: CommServerRequestState.Pending,
                    ApplicationId: this.telematicsAppToApply.Id,
                    Serial: device.Serial,
                    Expires: this.dateService.addOrSubtractTime(new Date, 1, DateUnit.Day),
                    UserId: this.currentUserService.cachedUser.Id,
                    VehicleId: device.vehicleMapping?.VehicleId
                }

                observablesArray.push(this.cameraApplicationRequestService.postApplicationRequest(newRequest));
            }

            if (this.updaterAppToApply?.Id) {
                const newRequest: IApplicationCameraRequest =
                {
                    Id: "",
                    ForceUpdate: this.forceUpdate,
                    RequestState: CommServerRequestState.Pending,
                    ApplicationId: this.updaterAppToApply.Id,
                    Serial: device.Serial,
                    Expires: this.dateService.addOrSubtractTime(new Date, 1, DateUnit.Day),
                    UserId: this.currentUserService.cachedUser.Id,
                    VehicleId: device.vehicleMapping?.VehicleId
                }

                observablesArray.push(this.cameraApplicationRequestService.postApplicationRequest(newRequest));
            }

            this.cancelSupersededRequests(observablesArray);
        });

        if (observablesArray.length > 0) {
            forkJoin(observablesArray)
                .pipe(
                    untilDestroyed(this),
                    tap(data => {
                        data.forEach(update => {
                            if (!!this.peripheral && !!update.ConfigurationId && update.RequestState === CommServerRequestState.Pending) {
                                this.peripheral.veriConfigUpdateHistory.push(update);
                            }

                            if (this.peripheral && !!update.ApplicationId && update.RequestState === CommServerRequestState.Pending) {
                                this.peripheral.veriAppUpdateHistory.push(update);
                            }
                        })

                        this.createTableRows();
                        this.cd.detectChanges();
                        this.onActionsSet.emit();
                    })
                ).subscribe();
        }
    }

    confirmApplySelectedActions(): void {
        if (!this.selectedDevices.length) {
            this.selectedDevices.push(this.peripheral);
        }

        this.constructChangesUpdateList();

        this.dialog = this.dialogService.open(this.confirmApplyTemplate, null);
    }

    closeDialog(): void {
        this.dialog.close();
    }

    setAutoUpdate(): void {
        this.onAutoUpdateSet.emit(this.peripheralMetadata.AutoUpdate);
    }

    private constructChangesUpdateList(): void {
        this.changeUpdateList = [];

        if (this.configToApply?.Id) {
            this.changeUpdateList.push(this.translationService.t("ConfigManagement.ConfigVersion", this.configToApply.Name));
        }

        if (this.updaterAppToApply?.Id) {
            this.changeUpdateList.push(this.translationService.t("ConfigManagement.UpdaterAppVersion", this.updaterAppToApply.Version));
        }

        if (this.telematicsAppToApply?.Id) {
            this.changeUpdateList.push(this.translationService.t("ConfigManagement.TelematicsAppVersion", this.telematicsAppToApply.Version));
        }

        if (this.forceUpdate) {
            this.changeUpdateList.push(this.translationService.t("ConfigManagement.ForceUpdateConfirmation"));
        }

        this.changeUpdateList.push(this.translationService.t("ConfigManagement.AutoCancelPending"))
    }

    private cancelSupersededRequests(observablesArray: Observable<any>[]): void {
        if (!this.peripheral) {
            return;
        }

        if (this.configToApply?.Id) {
            this.peripheral.veriConfigUpdateHistory?.filter(u => u.RequestState === CommServerRequestState.Pending && u?.Id)
                .forEach(update => {
                    update.RequestState = CommServerRequestState.Cancelled;

                    observablesArray.push(this.cameraConfigurationRequestService.postConfigurationRequest(update));
                });
        }

        if (this.telematicsAppToApply?.Id) {
            this.peripheral.veriAppUpdateHistory?.filter(u => this.cameraAppService.getAppType(u.ApplicationId, this.allApps) === CameraAppType.Telematics && u.RequestState === CommServerRequestState.Pending && u?.Id)
                .forEach(update => {
                    update.RequestState = CommServerRequestState.Cancelled;

                    observablesArray.push(this.cameraApplicationRequestService.postApplicationRequest(update));
                });
        }

        if (this.updaterAppToApply?.Id) {
            this.peripheral.veriAppUpdateHistory?.filter(u => this.cameraAppService.getAppType(u.ApplicationId, this.allApps) === CameraAppType.Updater && u.RequestState === CommServerRequestState.Pending && u?.Id)
                .forEach(update => {
                    update.RequestState = CommServerRequestState.Cancelled;

                    observablesArray.push(this.cameraApplicationRequestService.postApplicationRequest(update));
                });
        }
    }

    private loadData(): Observable<unknown> {
        this.loading = true;
        return combineLatest([
            this.cameraAppService.getApplicationFiles(),
            this.userService.getIdToNameMappings()
        ]).pipe(
            mergeMap(([apps, userList]) => {
                this.allApps = apps;
                this.availableTelematicsApps = this.allApps.filter(app => app.IsActive && app.CameraAppType === CameraAppType.Telematics);
                this.availableUpdaterApps = this.allApps.filter(app => app.IsActive && app.CameraAppType === CameraAppType.Updater);

                this.availableConfigs = this.allConfigs.filter(config => config.IsActive);
                this.userToNameList = userList;

                this.includeSetupOption();

                if (!this.isDeviceSpecific) {
                    this.loading = false;
                    return EMPTY;
                }

                this.createTableRows();
                this.loading = false;

                return of(void 0);
            }));
    }

    private includeSetupOption(): void {
        this.telematicsAppToApply = {
            Name: this.translationService.t('ConfigManagement.NoTelematicsUpdate'),
            IsActive: true,
            CameraAppType: CameraAppType.Telematics,
            Description: "",
            Id: "",
            State: CameraApplicationState.Production,
            Version: "1"
        };

        this.updaterAppToApply = {
            Name: this.translationService.t('ConfigManagement.NoUpdaterUpdate'),
            IsActive: true,
            CameraAppType: CameraAppType.Updater,
            Description: "",
            Id: "",
            State: CameraApplicationState.Production,
            Version: "1"
        };

        this.configToApply = {
            Config: "",
            Id: "",
            IsActive: true,
            Name: this.translationService.t('ConfigManagement.NoConfigUpdate'),
            Version: 1,
            VersionGroup: "1"
        };

        this.availableConfigs.push(this.configToApply);
        this.availableTelematicsApps.push(this.telematicsAppToApply);
        this.availableUpdaterApps.push(this.updaterAppToApply);
    }

    private createTables(): void {
        this.configHistoryTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Config" }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.Description, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", sorted: false }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.Status)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.RequestedDate", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.RequestedDate, { dateTime: true })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.InstalledDate", sortable: false }),
                createCellFunction: data => this.vTableService.createDateCell(data.InstalledDate ?? null, { dateTime: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.RequestedBy" }),
                createCellFunction: data => this.vTableService.createTableCell(data.RequestedUserName, { tooltip: true })
            },
        ];

        this.updaterHistoryTableColumns = this.telematicsHistoryTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "Version" }),
                createCellFunction: (data) => this.vTableService.createTableCell(this.allApps.find(a => a.Id == data.ApplicationId).Version, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", sorted: false }),
                createCellFunction: (data) => this.vTableService.createTableCell(CommServerRequestState[data.RequestState])
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.RequestedDate", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.CreatedTime, { dateTime: true })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.InstalledDate", sortable: false }),
                createCellFunction: data => this.vTableService.createDateCell(data.RequestState === CommServerRequestState.Completed ? data.UpdatedTime : null, { dateTime: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.RequestedBy" }),
                createCellFunction: data => this.vTableService.createTableCell(this.getUserNameFromId(data.UserId), { tooltip: true })
            },
        ];
    }

    private createTableRows(): void {
        if (!this.isDeviceSpecific) {
            return;
        }

        const combinedConfigUpdates: ICombinedConfigUpdate[] = [];

        this.configUpdateHistory?.forEach(update => {
            combinedConfigUpdates.push({
                Description: update.Description,
                Status: update.Status,
                RequestedDate: update.RequestedDate,
                InstalledDate: update.InstalledDate,
                RequestedUserName: update.RequestedUserName
            });
        });

        this.peripheral.veriConfigUpdateHistory?.forEach(update => {
            combinedConfigUpdates.push({
                Description: this.allConfigs.find(c => c.Id == update.ConfigurationId).Name,
                Status: CommServerRequestState[update.RequestState],
                RequestedDate: update.CreatedTime,
                InstalledDate: update.RequestState === CommServerRequestState.Completed ? update.UpdatedTime : null,
                RequestedUserName: this.getUserNameFromId(update.UserId)
            })
        });

        this.configHistoryTableRows = this.vTableService.createTableRows(combinedConfigUpdates, this.configHistoryTableColumns);

        this.updaterHistoryTableRows = this.vTableService.createTableRows(this.peripheral.veriAppUpdateHistory.filter(update => this.cameraAppService.getAppType(update.ApplicationId, this.allApps) === CameraAppType.Updater), this.updaterHistoryTableColumns);
        this.telematicsHistoryTableRows = this.vTableService.createTableRows(this.peripheral.veriAppUpdateHistory.filter(update => this.cameraAppService.getAppType(update.ApplicationId, this.allApps) === CameraAppType.Telematics), this.telematicsHistoryTableColumns);
    }

    private getUserNameFromId(userId: number): string {
        const rawName = this.userToNameList.find(u => u.UserId == userId)?.Username ?? "Unknown";

        return rawName.split('@')[0]
            .split('.')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    // CommmServer Communications - to be deletaed once shut down

    private getConfigFilesForDevice(): void {
        if (!this.deviceTypeId) {
            return;
        }

        this.selectedConfigFile = null;

        this.cameraCommunicationServer.getAvailableConfigFiles(this.deviceTypeId, true)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: configFiles => {
                    this.configFiles = configFiles.filter(file => !file.Archived).sort((a, b) => {
                        if (a.ConfigFileName < b.ConfigFileName) {
                            return -1;
                        }
                        if (a.ConfigFileName > b.ConfigFileName) {
                            return 1;
                        }
                        return 0;
                    });

                    this.selectCurrentConfig();
                },
                error: (error) => console.log(error)
            })
    }

    private selectCurrentConfig(): void {
        this.selectedConfigFile = this.configFiles.find(configFile => configFile.ConfigFileId == this.currentConfigId);
    }

    private getConfigHistory(): Observable<void> {
        if (isNaN(this.deviceTypeId) || this.configUpdateHistory?.length) {
            return;
        }

        this.cameraCommunicationServer
            .getConfigurationUpdateHistory(this.deviceId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (configHistory) => (this.configUpdateHistory = configHistory),
                error: (error) => console.log(error),
                complete: () => {
                    this.createTableRows();
                }
            })
    }

    private getConfigDataFromCommserver(): Observable<void> {
        return forkJoin({
            deviceInfo: this.cameraCommunicationServer.getDeviceInfo(this.peripheral.Serial),
        })
            .pipe(
                mergeMap(data => {
                    this.deviceTypeId = data.deviceInfo?.DeviceTypeId;
                    this.deviceId = data.deviceInfo?.DeviceId;
                    this.currentConfigId = data.deviceInfo?.ConfigId;

                    return this.cameraCommunicationServer.getConfigurationUpdateHistory(this.deviceId);
                }),
                map(() => {
                    this.getConfigFilesForDevice();
                    this.getConfigHistory();

                    this.loadingTable = false;

                    return;
                })
            );
    }

    registerConfigUpdateToCommServer(): void {


        const param = {
            DeviceId: this.deviceId,
            ConfigFileId: this.selectedConfigFile.ConfigFileId,
        };

        this.cameraCommunicationServer
            .postConfigUpdate(param)
            .pipe(
                untilDestroyed(this)
            )
            .subscribe({
                complete: () => {
                    this.currentConfigId = this.selectedConfigFile.ConfigFileId;
                    this.getConfigHistory();
                },
                error: (error) => {
                    console.error(error)
                }
            });

    }
}

// Short lived interface to be deleted when Commserver is deprecated
// Used to provide the UI with a unified interface for config updates
// while we have two separate datasources/models
interface ICombinedConfigUpdate {
    Description: string;
    Status: string;
    RequestedDate: Date;
    InstalledDate?: Date;
    RequestedUserName: string;
}
